/**
 * cart-drawer.js (vanilla + 2ml enforcement)
 *
 * Adds one thing to Dawn's vanilla cart drawer:
 * - If the cart contains ONLY "restricted" items, remove them immediately and re-render the drawer.
 * - Restricted items = (A) any line with variant option value "2ml" OR (B) any line with properties._rule_id === "BUYX_GETY_DISCOUNT"
 * - Optional exception kept: if cart contains ONLY Discovery Set + restricted items, remove restricted items.
 *
 * Why here? renderContents() is the one lifecycle that always runs after the drawer updates.
 */

const DISCOVERY_SET_VARIANT_ID = 56344936546678;
const TWO_ML_VALUE = '2ml';
const PROMO_RULE_ID = 'BUYX_GETY_DISCOUNT';

// simple lock to avoid loops while we remove + re-render
let __twoMlEnforceLock = false;

// ---------- Helpers ----------
async function fetchCartJSON() {
  const res = await fetch(`/cart.js?ts=${Date.now()}`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error('Failed to fetch cart');
  return res.json();
}

// Most reliable removal method in Shopify carts: line number (1-based)
async function removeLineItemByLine(lineNumber) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ line: lineNumber, quantity: 0 }),
  });
  if (!res.ok) throw new Error('Failed to remove line item');
  return res.json();
}

// Force re-render via Section Rendering API (cart-drawer + icon bubble)
async function forceCartDrawerRerender() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || typeof drawer.renderContents !== 'function') return false;

  const rootPath =
    (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';

  const url = `${rootPath}?sections=cart-drawer,cart-icon-bubble`;

  const [sectionsRes, cart] = await Promise.all([
    fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }),
    fetchCartJSON().catch(() => null),
  ]);

  if (!sectionsRes.ok) return false;

  const sections = await sectionsRes.json();
  const itemCount = cart && typeof cart.item_count === 'number' ? cart.item_count : null;

  drawer.renderContents({ id: null, sections, cartItemCount: itemCount });
  return true;
}

function isTwoMlCartItem(item) {
  const target = String(TWO_ML_VALUE).trim().toLowerCase();

  // Common in /cart.js
  if (Array.isArray(item?.variant_options)) {
    return item.variant_options.some((v) => String(v).trim().toLowerCase() === target);
  }

  // Sometimes present depending on theme/app
  if (Array.isArray(item?.options_with_values)) {
    return item.options_with_values.some((ov) => String(ov?.value).trim().toLowerCase() === target);
  }

  // Fallback: variant title contains 2ml
  if (item?.variant_title) {
    return String(item.variant_title).toLowerCase().includes(target);
  }

  return false;
}

function isPromoRuleItem(item) {
  return String(item?.properties?._rule_id || '') === String(PROMO_RULE_ID);
}

function isRestrictedItem(item) {
  return isTwoMlCartItem(item) || isPromoRuleItem(item);
}

// ---------- Enforcement ----------
/**
 * Rules:
 * A) If cart contains ONLY restricted items -> remove them
 * B) If cart contains ONLY Discovery Set (any qty) + restricted items -> remove restricted items
 */
async function enforceRestrictedEligibility() {
  if (__twoMlEnforceLock) return;
  __twoMlEnforceLock = true;

  try {
    const cart = await fetchCartJSON();
    const items = Array.isArray(cart.items) ? cart.items : [];
    if (!items.length) return;

    const restrictedLineNumbers = [];
    const nonRestrictedItems = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (isRestrictedItem(item)) restrictedLineNumbers.push(i + 1); // 1-based
      else nonRestrictedItems.push(item);
    }

    if (!restrictedLineNumbers.length) return;

    const restrictedOnly = nonRestrictedItems.length === 0;

    const discoveryOnly =
      nonRestrictedItems.length === 1 &&
      Number(nonRestrictedItems[0].variant_id) === Number(DISCOVERY_SET_VARIANT_ID);

    if (!restrictedOnly && !discoveryOnly) return;

    // Remove bottom -> top so line indices remain stable
    restrictedLineNumbers.sort((a, b) => b - a);

    for (const lineNo of restrictedLineNumbers) {
      await removeLineItemByLine(lineNo);
    }

    // Re-render drawer so UI updates immediately
    await forceCartDrawerRerender();
  } catch (e) {
    console.warn('[CartDrawer] Restricted eligibility enforcement failed:', e);
  } finally {
    __twoMlEnforceLock = false;
  }
}

// ---------- Vanilla CartDrawer ----------
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        // If parsedState provided cartItemCount, we may have toggled is-empty.
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

close() {
  this.classList.remove('active');

  const elementToRestore = this.activeElement;

  // Remove the focus trap without allowing the utility
  // to focus an element and scroll it into view.
  removeTrapFocus();

  document.body.classList.remove('overflow-hidden');

  if (elementToRestore && elementToRestore.isConnected) {
    elementToRestore.focus({ preventScroll: true });
  }

  this.activeElement = null;
}

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Maintain empty state when we call forceCartDrawerRerender()
    const itemCount =
      parsedState && typeof parsedState.cartItemCount === 'number' ? parsedState.cartItemCount : null;

    if (itemCount !== null) {
      const isEmpty = itemCount === 0;
      this.classList.toggle('is-empty', isEmpty);
      const inner = this.querySelector('.drawer__inner');
      if (inner) inner.classList.toggle('is-empty', isEmpty);
    } else {
      this.querySelector('.drawer__inner')?.classList.contains('is-empty') &&
        this.querySelector('.drawer__inner').classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // ✅ run enforcement after every AJAX drawer render
    enforceRestrictedEligibility();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);